import React from 'react';
import PropTypes from 'prop-types';
import ReactDOMServer from 'react-dom/server';

import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter, StaticRouter } from 'react-router-dom';
import { Provider } from 'react-redux';
import mapValues from 'lodash/mapValues';
import moment from 'moment';
// French month and day names for moment; moment.locale() below picks the page's locale.
import 'moment/locale/fr';
import { IntlProvider } from './util/reactIntl';
import configureStore from './store';
import routeConfiguration from './routeConfiguration';
import Routes from './Routes';
import OfflineBanner from './components/OfflineBanner/OfflineBanner';
import { DEFAULT_LOCALE, basenameForLocale } from './util/locale';

// Flex template application uses English translations as default translations.
import englishMessages from './translations/en.json';

// If translation key is missing from the target language (e.g. fr.json),
// the corresponding key is filled in from the default language (en.json)
// to prevent missing-translation errors.
//
// PERFORMANCE NOTE: this used to be a per-missing-key reduce that spread
// the ENTIRE accumulated object on every step — O(n²) over ~3,500 keys —
// and it was invoked per SSR request (see loadLocaleMessages below). That
// burned ~1.6s of synchronous CPU per request on a laptop (4-5s on a
// Heroku dyno), blocking the event loop and queueing every concurrent
// request behind it. A single object spread does the same job in O(n):
// source provides the base, target overrides where it has a value.
const addMissingTranslations = (sourceLangTranslations, targetLangTranslations) => ({
  ...sourceLangTranslations,
  ...targetLangTranslations,
});

const isTestEnv = process.env.NODE_ENV === 'test';

const buildLocaleMessages = (locale, frenchMessages) => {
  const defaultMessages = locale === 'fr' ? frenchMessages || {} : englishMessages;
  if (isTestEnv) {
    // Tests use the key as the value so assertions work regardless of locale
    return mapValues(defaultMessages, (val, key) => key);
  }
  // English fills any gaps; the active locale's own strings win.
  return addMissingTranslations(englishMessages, defaultMessages);
};

// English is always bundled, so the English case is available synchronously.
// Computed ONCE at module load — never per request/render.
const localeMessages = buildLocaleMessages(DEFAULT_LOCALE);

// Memoized French build — fr.json is code-split so it only loads for
// French users, and the merge runs once per process, not per request.
let frLocaleMessagesPromise = null;

// Load a locale's messages. Called by the client entry (index.js) before
// hydrate and by the server for each request — both hit the
// precomputed/memoized values, so this is O(1) per request.
export const loadLocaleMessages = locale => {
  if (locale !== 'fr') {
    return Promise.resolve(localeMessages);
  }
  if (!frLocaleMessagesPromise) {
    frLocaleMessagesPromise = import(
      /* webpackChunkName: "fr-translations" */ './translations/fr.json'
    ).then(mod => buildLocaleMessages('fr', mod.default || mod));
  }
  return frLocaleMessagesPromise;
};

// moment's locale is global; both apps set it right before rendering, which is synchronous.
// Tests always render in English.
const setupMoment = locale => moment.locale(isTestEnv ? DEFAULT_LOCALE : locale);

export const ClientApp = props => {
  const { store, messages, locale = DEFAULT_LOCALE } = props;
  setupMoment(locale);

  const translationMessages = messages || localeMessages;

  return (
    <IntlProvider locale={locale} messages={{ ...translationMessages }} textComponent="span">
      <Provider store={store}>
        <HelmetProvider>
          <BrowserRouter basename={basenameForLocale(locale)}>
            <OfflineBanner />
            <Routes routes={routeConfiguration()} />
          </BrowserRouter>
        </HelmetProvider>
      </Provider>
    </IntlProvider>
  );
};

const { any, string } = PropTypes;

ClientApp.propTypes = { store: any.isRequired, locale: string };

export const ServerApp = props => {
  const { url, context, helmetContext, store, messages, locale = DEFAULT_LOCALE } = props;

  const translationMessages = messages || localeMessages;

  setupMoment(locale);
  HelmetProvider.canUseDOM = false;
  return (
    <IntlProvider locale={locale} messages={{ ...translationMessages }} textComponent="span">
      <Provider store={store}>
        <HelmetProvider context={helmetContext}>
          <StaticRouter basename={basenameForLocale(locale)} location={url} context={context}>
            <OfflineBanner />
            <Routes routes={routeConfiguration()} />
          </StaticRouter>
        </HelmetProvider>
      </Provider>
    </IntlProvider>
  );
};

ServerApp.propTypes = {
  url: string.isRequired,
  context: any.isRequired,
  store: any.isRequired,
  locale: string,
};

/**
 * Render the given route.
 *
 * @param {String} url Path to render, with its locale prefix (/fr/...)
 * @param {Object} serverContext Server rendering context from react-router
 * @param {String} locale The page's locale, from the URL (util/locale)
 *
 * @returns {Object} Object with keys:
 *  - {String} body: Rendered application body of the given route
 *  - {Object} head: Application head metadata from react-helmet
 */
export const renderApp = (url, serverContext, preloadedState, collectChunks, messages, locale) => {
  // Don't pass an SDK instance since we're only rendering the
  // component tree with the preloaded store state and components
  // shouldn't do any SDK calls in the (server) rendering lifecycle.
  const store = configureStore({ initialState: preloadedState });

  const helmetContext = {};

  // When rendering the app on server, we wrap the app with webExtractor.collectChunks
  // This is needed to figure out correct chunks/scripts to be included to server-rendered page.
  // https://loadable-components.com/docs/server-side-rendering/#3-setup-chunkextractor-server-side
  const WithChunks = collectChunks(
    <ServerApp
      url={url}
      context={serverContext}
      helmetContext={helmetContext}
      store={store}
      messages={messages}
      locale={locale}
    />
  );
  const body = ReactDOMServer.renderToString(WithChunks);
  const { helmet: head } = helmetContext;
  return { head, body };
};
